import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, getToken } from "../api";
import StickyHead from "../StickyHead";

const STATUSES = [
  ["PENDING", "입금 대기"],
  ["PAID", "입금 확인"],
  ["SHIPPED", "배송 중"],
  ["DELIVERED", "배송 완료"],
  ["CANCELLED", "취소"],
];
const STATUS_LABEL = Object.fromEntries(STATUSES);

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ko-KR", { timeZone: "Asia/Seoul", dateStyle: "medium", timeStyle: "short" });
}
const won = (n) => `${(n || 0).toLocaleString("ko-KR")}원`;

/* 펌웨어 개발의뢰 — 라오나봇 내 프로젝트에서 [펌웨어 빌드 포함]으로 주문한 의뢰 (백엔드 app/laona_fw.py).
   입금 확인(PAID 이후)으로 바꾸면 라오나빌드서버의 [빌드요청 가져오기]가 이 의뢰를 가져간다 */
const FW_READY = ["PAID", "SHIPPED", "DELIVERED"];

function FwPanel({ order, fw, onChanged }) {
  const [md, setMd] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function toggleMd() {
    if (md !== null) { setMd(null); return; }
    setError("");
    try {
      const res = await fetch(`/api/sstoa/shop-orders/fw/${encodeURIComponent(fw.request_no)}/request.md`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error("의뢰서를 불러오지 못했습니다.");
      setMd(await res.text());
    } catch (err) { setError(err.message); }
  }

  async function setStatus(to, label) {
    if (!window.confirm(`${fw.request_no} 의뢰를 '${label}'(으)로 바꿀까요?`)) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/shop-orders/fw/${encodeURIComponent(fw.request_no)}/status`, { to });
      await onChanged();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  const ready = FW_READY.includes(order.status);
  const where = fw.status === "ORDERED"
    ? (ready ? "입금 확인됨 — 라오나빌드서버에서 [빌드요청 가져오기]로 가져갈 수 있습니다."
      : order.status === "CANCELLED" ? "주문이 취소되었습니다." : "입금 확인 전 — 입금 확인으로 바꾸면 빌드 서버가 가져갈 수 있습니다.")
    : fw.status === "RECEIVED" ? `빌드 서버가 가져갔습니다${fw.received_at ? ` (${formatWhen(fw.received_at)})` : ""}.`
    : "";
  return (
    <div className="sstoa-fw">
      <div className="sstoa-fw__head">
        <span className="sstoa-tag sstoa-tag--fw">펌웨어 개발의뢰</span>
        <b style={{ fontFamily: "ui-monospace, monospace" }}>{fw.request_no}</b>
        <span>{fw.title}</span>
        <span className="sstoa-tag">{fw.label}</span>
        <span className="sstoa-hint">모듈 {fw.modules} · 연결 {fw.connections} · 빌드비 {won(fw.fee)}</span>
      </div>
      {where && <p className="sstoa-hint" style={{ margin: "6px 0" }}>{where}</p>}
      <div className="sstoa-actions">
        <button className="sstoa-btn sstoa-btn--sm" onClick={toggleMd}>{md === null ? "기능정의서 보기" : "기능정의서 닫기"}</button>
        {["RECEIVED", "IN_PROGRESS"].includes(fw.status) && (
          <>
            {fw.status === "RECEIVED" && (
              <button className="sstoa-btn sstoa-btn--sm" disabled={busy} onClick={() => setStatus("IN_PROGRESS", "개발 중")}>개발 중으로</button>
            )}
            <button className="sstoa-btn sstoa-btn--sm sstoa-btn--primary" disabled={busy} onClick={() => setStatus("DONE", "완료")}>완료로</button>
          </>
        )}
      </div>
      {fw.admin_note && <pre className="sstoa-fw__log">{fw.admin_note}</pre>}
      {md !== null && <pre className="sstoa-fw__md">{md}</pre>}
      {error && <div className="sstoa-error" style={{ marginTop: 6 }}>{error}</div>}
    </div>
  );
}

/* 지울 수 있는 주문 — 배송 완료 · 취소 (시험 주문 · 실수 주문 정리용). 지우면 회원 MY 주문내역에서도 사라진다 */
const DELETABLE = ["DELIVERED", "CANCELLED"];

/* 삭제 확인 → { restore_stock } 또는 null(그만둠) */
function askDelete(item) {
  const pi = item.pay_info || {};
  const realCard = item.pay_method === "CARD" && item.paid_at && !pi.test && !pi.refund;
  const msg = [
    `주문 #${item.id} 을(를) 완전히 삭제할까요?`,
    "회원의 주문내역에서도 사라지고 되돌릴 수 없습니다.",
    realCard ? "※ 실제 카드결제(환불 안 됨) 주문입니다. 포트원의 결제 기록은 그대로 남습니다." : "",
  ].filter(Boolean).join("\n");
  if (!window.confirm(msg)) return null;
  const restore = item.stock_taken
    ? window.confirm("이 주문으로 빠졌던 재고도 되돌릴까요?\n시험 주문이면 [확인], 실제로 발송한 주문이면 [취소]를 누르세요.")
    : false;
  return { restore_stock: restore };
}

/* 목록 한 줄 — 누르면 상세 팝업(OrderModal)이 열린다. 방금 본 주문은 옅게 강조해 위치를 잃지 않게 한다 */
function Row({ item, viewed, onOpen, onDelete }) {
  const first = item.items[0];
  const more = item.items.length - 1;
  const card = item.pay_method === "CARD";
  const pi = item.pay_info || {};
  return (
    <tr className={`sstoa-od-row${viewed ? " is-viewed" : ""}`} onClick={() => onOpen(item.id)}>
      <td>
        <span className={`sstoa-tag ${["DELIVERED", "CANCELLED"].includes(item.status) ? "sstoa-tag--gray" : ""}`}>
          {STATUS_LABEL[item.status] || item.status}
        </span>
      </td>
      <td style={{ fontFamily: "ui-monospace, monospace" }}>#{item.id}</td>
      <td style={{ fontWeight: 600 }}>
        {item.fw && item.fw.length > 0 && <span className="sstoa-tag sstoa-tag--fw" style={{ marginRight: 6 }}>펌웨어 개발 포함</span>}
        {first ? first.name : "—"}{more > 0 ? ` 외 ${more}건` : ""}
      </td>
      <td>
        {item.receiver_name}
        <div style={{ fontSize: 12.5, color: "var(--s-fg-mute)", marginTop: 2 }}>
          {card ? `카드결제${pi.status === "PAID" || item.paid_at ? "" : " · 결제 전"}${pi.test ? " · 테스트" : ""}${pi.problem ? " · ⚠ 확인 필요" : ""}` : `입금자 ${item.depositor}`}
        </div>
      </td>
      <td style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{won(item.total)}</td>
      <td>{formatWhen(item.created_at)}</td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        {DELETABLE.includes(item.status) && (
          <button type="button" className="sstoa-btn sstoa-btn--sm sstoa-btn--danger"
                  onClick={(e) => { e.stopPropagation(); onDelete(item); }} title="DB 에서 지웁니다 — 회원 주문내역에서도 사라집니다">
            삭제
          </button>
        )}
      </td>
    </tr>
  );
}

function Sec({ title, children }) {
  return (
    <section className="sstoa-od__sec">
      <h3 className="sstoa-od__h">{title}</h3>
      {children}
    </section>
  );
}

/* 주문 상세 팝업 — 머리(주문번호 · 상태 · 금액 · 이전/다음), 본문 구획, 아래 고정 줄(상태 변경 · 송장 · 메모 저장).
   상태를 바꿔도 닫히지 않고, 뒤의 목록과 탭 숫자도 함께 바뀐다. Esc · 바깥 클릭으로 닫고 ← → 로 옆 주문 */
function OrderModal({ item, pos, total, onPrev, onNext, onClose, onPatch, onReload, onDelete }) {
  const [tracking, setTracking] = useState(item.tracking || "");
  const [memo, setMemo] = useState(item.admin_memo || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const dirty = tracking !== (item.tracking || "") || memo !== (item.admin_memo || "");

  const leave = useCallback((go) => {
    if (dirty && !window.confirm("저장하지 않은 송장 · 메모가 있습니다. 그대로 넘어갈까요?")) return;
    go();
  }, [dirty]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { leave(onClose); return; }
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      if (e.key === "ArrowLeft" && onPrev) leave(onPrev);
      if (e.key === "ArrowRight" && onNext) leave(onNext);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [leave, onClose, onPrev, onNext]);

  async function patch(p, confirmMsg) {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    setError("");
    try { await onPatch(item.id, p); } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  const card = item.pay_method === "CARD";
  const pi = item.pay_info || {};
  const hasFw = item.fw && item.fw.length > 0;

  return (
    <div className="sstoa-modal" role="dialog" aria-modal="true" aria-label={`주문 #${item.id}`}
         onMouseDown={(e) => { if (e.target === e.currentTarget) leave(onClose); }}>
      <div className="sstoa-modal__box sstoa-od">
        <div className="sstoa-modal__head">
          <div className="sstoa-od__title">
            <h2 className="sstoa-modal__title">주문 #{item.id}</h2>
            <span className={`sstoa-tag ${["DELIVERED", "CANCELLED"].includes(item.status) ? "sstoa-tag--gray" : ""}`}>
              {STATUS_LABEL[item.status] || item.status}
            </span>
            <span className="sstoa-tag sstoa-tag--gray">{card ? `카드결제${pi.test ? " · 테스트" : ""}` : "계좌이체"}</span>
            {hasFw && <span className="sstoa-tag sstoa-tag--fw">펌웨어 개발 포함</span>}
            <b className="sstoa-od__total">{won(item.total)}</b>
          </div>
          <div className="sstoa-od__nav">
            <button type="button" className="sstoa-btn sstoa-btn--sm" disabled={!onPrev} onClick={() => leave(onPrev)} title="이전 주문 (←)">‹ 이전</button>
            {total > 0 && <span className="sstoa-hint">{pos + 1} / {total}</span>}
            <button type="button" className="sstoa-btn sstoa-btn--sm" disabled={!onNext} onClick={() => leave(onNext)} title="다음 주문 (→)">다음 ›</button>
            <button type="button" className="sstoa-modal__x" onClick={() => leave(onClose)} aria-label="닫기">×</button>
          </div>
        </div>

        <div className="sstoa-modal__body">
          {pi.problem && (
            <div className="sstoa-error" style={{ marginBottom: 12 }}>
              카드결제 확인 필요 — {pi.problem} 포트원 콘솔에서 결제 내역을 확인한 뒤 처리해 주세요.
            </div>
          )}

          <Sec title="주문 상품">
            <table className="sstoa-table sstoa-od__items">
              <tbody>
                {item.items.map((i) => (
                  <tr key={i.slug}>
                    <td className="sstoa-od__code" style={{ fontFamily: "ui-monospace, monospace", whiteSpace: "nowrap" }}>{i.code}</td>
                    <td>{i.name}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{won(i.price)} × {i.qty}</td>
                    <td style={{ textAlign: "right", fontWeight: 600, whiteSpace: "nowrap" }}>{won(i.price * i.qty)}</td>
                  </tr>
                ))}
                <tr className="sstoa-od__sum">
                  <td className="sstoa-od__code" />
                  <td colSpan={2} style={{ textAlign: "right" }}>
                    상품{hasFw ? " · 펌웨어 개발" : ""} {won(item.subtotal)} + 배송비 {item.shipping ? won(item.shipping) : "무료"}
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>{won(item.total)}</td>
                </tr>
              </tbody>
            </table>
          </Sec>

          {hasFw && (
            <Sec title="펌웨어 개발의뢰">
              {item.fw.map((f) => <FwPanel key={f.request_no} order={item} fw={f} onChanged={onReload} />)}
            </Sec>
          )}

          <div className="sstoa-od__cols">
            <Sec title="결제">
              <dl className="sstoa-meta">
                <dt>결제 수단</dt>
                <dd>{card ? `카드결제${pi.card ? ` · ${pi.card}` : ""}${pi.installment ? ` · ${pi.installment}개월` : ""}` : "계좌이체"}{pi.test ? " (테스트 결제)" : ""}</dd>
                {card ? (
                  <>
                    {pi.approval_no && <><dt>승인번호</dt><dd>{pi.approval_no}{pi.pg ? ` · ${pi.pg}` : ""}</dd></>}
                    {item.payment_id && <><dt>결제 ID</dt><dd style={{ fontFamily: "ui-monospace, monospace", fontSize: 12.5 }}>{item.payment_id}</dd></>}
                    {pi.receipt_url && <><dt>영수증</dt><dd><a href={pi.receipt_url} target="_blank" rel="noreferrer">카드 영수증 열기</a></dd></>}
                    {pi.refund && <><dt>환불</dt><dd>{pi.refund}{pi.refund_amount ? ` · ${won(pi.refund_amount)}` : ""}</dd></>}
                  </>
                ) : (
                  <><dt>입금자명</dt><dd>{item.depositor || "—"}</dd></>
                )}
                <dt>주문일</dt><dd>{formatWhen(item.created_at)}</dd>
                {item.paid_at && <><dt>{card ? "결제 확인" : "입금 확인"}</dt><dd>{formatWhen(item.paid_at)}</dd></>}
                {item.shipped_at && <><dt>발송</dt><dd>{formatWhen(item.shipped_at)}</dd></>}
                {item.delivered_at && <><dt>배송 완료</dt><dd>{formatWhen(item.delivered_at)}</dd></>}
                {item.cancelled_at && <><dt>취소</dt><dd>{formatWhen(item.cancelled_at)}</dd></>}
                <dt>재고</dt><dd>{item.stock_taken ? "차감됨" : "차감 전"}</dd>
              </dl>
            </Sec>
            <Sec title="주문자 · 배송">
              <dl className="sstoa-meta">
                <dt>회원</dt><dd>{item.member ? `${item.member.name} (${item.member.email})` : "—"}</dd>
                <dt>받는 분</dt><dd>{item.receiver_name}</dd>
                <dt>연락처</dt><dd>{item.phone}</dd>
                <dt>이메일</dt><dd>{item.email}</dd>
                <dt>배송지</dt><dd>{item.zip ? `(${item.zip}) ` : ""}{item.address} {item.address2}</dd>
                {item.memo && <><dt>배송 메모</dt><dd>{item.memo}</dd></>}
              </dl>
            </Sec>
          </div>

          <Sec title="송장 · 처리 메모">
            <div className="sstoa-row" style={{ marginBottom: 0 }}>
              <div className="sstoa-field">
                <label>송장 · 택배사</label>
                <input className="sstoa-input" value={tracking} onChange={(e) => setTracking(e.target.value)} disabled={busy} placeholder="CJ대한통운 1234-5678-9012" />
              </div>
              <div className="sstoa-field" style={{ flex: 2 }}>
                <label>처리 메모 <span className="sstoa-hint">— 회원에게는 보이지 않습니다</span></label>
                <input className="sstoa-input" value={memo} onChange={(e) => setMemo(e.target.value)} disabled={busy} />
              </div>
            </div>
          </Sec>
        </div>

        <div className="sstoa-modal__foot">
          {error && <div className="sstoa-error sstoa-modal__error">{error}</div>}
          <button className={`sstoa-btn sstoa-btn--sm${dirty ? " sstoa-btn--primary" : ""}`} onClick={() => patch({ tracking, admin_memo: memo })} disabled={busy || !dirty}>
            송장 · 메모 저장
          </button>
          <span className="sstoa-od__sep" />
          <span className="sstoa-hint">상태 변경</span>
          {STATUSES.map(([v, l]) => (
            <button
              key={v} disabled={busy || item.status === v}
              className={`sstoa-btn sstoa-btn--sm${item.status === v ? " sstoa-btn--primary" : ""}${v === "CANCELLED" ? " sstoa-btn--danger" : ""}`}
              onClick={() => patch(
                v === "SHIPPED" ? { status: v, tracking } : { status: v },
                v === "CANCELLED" ? `주문 #${item.id} 을(를) 취소할까요?${item.stock_taken ? " 뺐던 재고는 되돌립니다." : ""}${card && item.paid_at ? " 카드결제 금액은 전액 환불됩니다." : ""}` : null,
              )}
            >
              {l}
            </button>
          ))}
          <span className="sstoa-hint sstoa-od__foot-hint">입금 확인 → 재고 차감 · 취소 → 되돌림 · 배송 완료 → 회원 구매내역</span>
          {DELETABLE.includes(item.status) && (
            <button className="sstoa-btn sstoa-btn--sm sstoa-btn--danger sstoa-od__del" disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      setError("");
                      try { await onDelete(item); } catch (err) { setError(err.message); } finally { setBusy(false); }
                    }}
                    title="DB 에서 지웁니다 — 회원 주문내역에서도 사라집니다">
              주문 삭제
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ShopOrdersAdmin() {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState(null);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");
  const [params, setParams] = useSearchParams();
  const openId = Number(params.get("order")) || null;
  const [seen, setSeen] = useState({});      // id → 주문 (목록에서 빠져도 팝업은 마지막 내용을 보인다)
  const [navIds, setNavIds] = useState([]);  // 팝업을 열 때의 목록 순서 — 이전 / 다음
  const [viewed, setViewed] = useState(null);

  const remember = (list) => setSeen((prev) => {
    const next = { ...prev };
    for (const o of list) next[o.id] = o;
    return next;
  });

  async function load(status = filter) {
    setError("");
    try {
      const [list, c] = await Promise.all([
        api.get(`/shop-orders${status ? `?status=${status}` : ""}`),
        api.get("/shop-orders/counts"),
      ]);
      setRows(list);
      setCounts(c);
      remember(list);
      return list;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }

  useEffect(() => {
    load("").then((list) => {
      // 주소로 바로 열었으면(텔레그램 알림 링크 등) 그 주문 — 목록 순서로 이전 / 다음
      if (list && openId) setNavIds(list.map((o) => o.id));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onFilter(s) { setFilter(s); load(s); }

  function open(id) {
    setNavIds((rows || []).map((o) => o.id));
    setViewed(id);
    setParams((p) => { const n = new URLSearchParams(p); n.set("order", String(id)); return n; });
  }
  function go(id) {
    setViewed(id);
    setParams((p) => { const n = new URLSearchParams(p); n.set("order", String(id)); return n; }, { replace: true });
  }
  function close() {
    if (openId) setViewed(openId);
    setParams((p) => { const n = new URLSearchParams(p); n.delete("order"); return n; });
  }

  /* 삭제 — 확인 후 DB 에서 지우고, 팝업이 떠 있었으면 닫는다. 실패하면 예외(팝업이 오류를 보인다) */
  async function remove(item) {
    const opt = askDelete(item);
    if (!opt) return;
    await api.del(`/shop-orders/${item.id}${opt.restore_stock ? "?restore_stock=true" : ""}`);
    setNavIds((ids) => ids.filter((x) => x !== item.id));
    setSeen((prev) => { const n = { ...prev }; delete n[item.id]; return n; });
    if (openId === item.id) setParams((p) => { const n = new URLSearchParams(p); n.delete("order"); return n; });
    await load();
  }
  async function removeFromList(item) {
    try { await remove(item); } catch (err) { setError(err.message); }
  }

  async function change(id, patch) {
    const updated = await api.post(`/shop-orders/${id}`, patch); // 실패하면 팝업이 오류를 보인다
    remember([updated]);
    await load();
  }

  // 팝업이 떠 있는 동안 뒤 화면은 스크롤되지 않게
  useEffect(() => {
    if (!openId) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [openId]);

  const current = openId ? seen[openId] : null;
  const pos = openId ? navIds.indexOf(openId) : -1;
  const prevId = pos > 0 ? navIds[pos - 1] : null;
  const nextId = pos >= 0 && pos < navIds.length - 1 ? navIds[pos + 1] : null;

  return (
    <div>
      <StickyHead>
        <div style={{ marginBottom: 12 }}>
          <Link to="/sstoa" className="sstoa-back">← 홈</Link>
        </div>

        <div className="sstoa-topbar">
          <div>
            <h1 className="sstoa-title">판매 주문</h1>
            <p className="sstoa-sub">홈페이지 판매제품 주문입니다. 입금을 확인하면 상태를 바꿔 주세요. 행을 누르면 상세 내용이 팝업으로 열립니다.</p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <button className={`sstoa-btn sstoa-btn--sm${filter === "" ? " sstoa-btn--primary" : ""}`} onClick={() => onFilter("")}>
            전체{counts ? ` ${counts.total}` : ""}
          </button>
          {STATUSES.map(([v, l]) => (
            <button key={v} className={`sstoa-btn sstoa-btn--sm${filter === v ? " sstoa-btn--primary" : ""}`} onClick={() => onFilter(v)}>
              {l}{counts ? ` ${counts[v] || 0}` : ""}
            </button>
          ))}
        </div>
      </StickyHead>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : rows.length === 0 ? (
          <div className="sstoa-empty">주문이 없습니다.</div>
        ) : (
          <table className="sstoa-table">
            <thead>
              <tr><th>상태</th><th>주문</th><th>상품</th><th>받는 분</th><th>금액</th><th>주문일</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((it) => <Row key={it.id} item={it} viewed={viewed === it.id || openId === it.id} onOpen={open} onDelete={removeFromList} />)}
            </tbody>
          </table>
        )}
      </div>

      {openId && rows !== null && !current && (
        <div className="sstoa-modal" role="dialog" aria-modal="true" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
          <div className="sstoa-modal__box" style={{ maxWidth: 420 }}>
            <div className="sstoa-modal__head">
              <h2 className="sstoa-modal__title">주문 #{openId}</h2>
              <button type="button" className="sstoa-modal__x" onClick={close} aria-label="닫기">×</button>
            </div>
            <div className="sstoa-modal__body">주문을 찾을 수 없습니다.</div>
          </div>
        </div>
      )}
      {current && (
        <OrderModal
          key={current.id}
          item={current}
          pos={pos}
          total={navIds.length}
          onPrev={prevId ? () => go(prevId) : null}
          onNext={nextId ? () => go(nextId) : null}
          onClose={close}
          onPatch={change}
          onReload={() => load()}
          onDelete={remove}
        />
      )}
    </div>
  );
}
